/**
 * T17307 wiring · degraded-poll resume catch-up.
 *
 * When the connectivity FSM is in `DEGRADED_NO_NATS` (REST-poll fallback
 * active) and the app/tab returns to the foreground, a poll tick may
 * have elapsed while the document was hidden. TanStack pauses
 * `refetchInterval` in the background and, on resume, schedules the
 * NEXT tick a full interval out — no immediate catch-up — so the UI
 * serves stale data until that next interval fires.
 *
 * This installer subscribes to the platform `appState` foreground-return
 * events and, per active realtime query, fires EXACTLY ONE catch-up
 * fetch iff the query is overdue, then lets the function-form
 * `refetchInterval` (`degradedPollInterval`) resume the normal cadence.
 * The pure decision contract lives in `poll-resume.ts` (§3: never
 * fire-and-schedule double).
 *
 * Platform-neutral: the `appState` listener is injected (the platform
 * adapter supplies the web/RN impl); no DOM/RN API is referenced here.
 * Wire once from the SDK boot sequence alongside the other
 * connectivity-lifecycle installers.
 */

import type { Query, QueryClient } from '@tanstack/react-query';
import { connectivityStore } from '../../core/connectivity-store.js';
import { isSubscriberQueryFn } from '../../core/internal/subscriber-query-fn.js';
import type { AppStateListener } from '../../platform/adapter.js';
import { shouldRecoveryRefetchRealtimeQuery } from '../reconnect/fan-out.js';
import { degradedPollInterval } from './degraded-poll-fallback.js';
import { resumePolling } from './poll-resume.js';

/** Minimal slice of the platform adapter's `appState` slot we depend on. */
export interface PollResumeAppState {
  addListener(cb: AppStateListener): () => void;
}

/**
 * Install the degraded-poll resume catch-up. Returns a teardown.
 */
export function installDegradedPollResume(
  queryClient: QueryClient,
  appState: PollResumeAppState,
): () => void {
  return appState.addListener((ev) => {
    // Foreground-return signals only. 'hidden' / 'freeze' = going away.
    if (ev.kind !== 'visible' && ev.kind !== 'resume' && ev.kind !== 'bfcache-restore') {
      return;
    }
    // Only relevant while REST-poll fallback is the live data path.
    if (connectivityStore.getState().state !== 'DEGRADED_NO_NATS') return;

    for (const query of queryClient.getQueryCache().getAll()) {
      if (query.meta?.pollingManaged === true) continue;
      if (query.observers.length === 0) continue; // unmounted → no UI to refresh
      if (!shouldRecoveryRefetchRealtimeQuery(queryClient, query)) continue;
      resumePolling(query as Query, {
        isOverdue: (q): boolean => {
          const interval = degradedPollInterval(q);
          if (interval === false) return false; // this query is not polling
          return Date.now() - q.state.dataUpdatedAt >= interval;
        },
        fetchOnce: (q): void => {
          // Blessed fetch path (mirrors `refetchRealtimeQuery`): refetch
          // the data-owning observer, skipping the realtime subscriber
          // observer (which has no payload to (re)fetch).
          for (const obs of q.observers) {
            if (isSubscriberQueryFn(obs.options.queryFn)) continue;
            void (obs as unknown as { refetch(): Promise<unknown> }).refetch();
            break;
          }
        },
        scheduleNext: (): void => {
          // No-op by design. TanStack's function-form `refetchInterval`
          // (`degradedPollInterval`) reschedules the next tick itself
          // once the observer re-evaluates after the focus-resume +
          // catch-up fetch. Explicit scheduling here would double-fire
          // (poll-resume.ts §3 "NEVER fire-and-schedule double").
        },
      });
    }
  });
}
